<template>
  <div>
    <i18n-t
      :keypath="getMessage()"
      tag="p"
      :class="{
        'flex items-center gap-1': true,
        'flex-wrap': allowDisplayInMultipleLines === true,
        'gap-3 break-normal': props.tagStyle === 'default',
      }">
      <!-- User Name-->
      <template v-if="userName || loadingUserName" #user>
        <SmTag
          :appearance="props.tagStyle"
          :label="userName"
          :icon="User"
          :class="{
            'cursor-pointer hover:underline':
              props.clickable && props.tagStyle === 'text',
            'cursor-pointer transition hover:bg-gray-300 dark:hover:bg-gray-500':
              props.clickable && props.tagStyle !== 'text',
            'cursor-default': !props.clickable,
          }"
          :hide-tooltip="props.clickable"
          :loading="loadingUserName"
          @click="props.clickable ? openUserDetailsDrawer(userId) : null" />
      </template>

      <!-- Software Name -->
      <template v-if="applicationName || loadingApplicationName" #software>
        <SmTag
          :appearance="props.tagStyle"
          :label="applicationName"
          :icon="LayoutGrid"
          :loading="loadingApplicationName">
          <template #label>
            <div class="flex gap-2">
              <ApplicationIcon
                v-if="applicationName && props.tagStyle === 'default'"
                :application-name="applicationName"
                :size="18" />
              <TextMultiline :text="applicationName" />
            </div>
          </template>
        </SmTag>
      </template>

      <!-- License Email -->
      <template v-if="licenseEmail || loadingLicenseEmail" #license>
        <SmTag
          :appearance="props.tagStyle"
          :label="licenseEmail"
          :icon="File"
          :loading="loadingLicenseEmail" />
      </template>

      <!-- Software License Name -->
      <template
        v-if="softwareLicenseName || loadingSoftwareLicenseName"
        #softwarelicense>
        <SmTag
          :appearance="props.tagStyle"
          :label="softwareLicenseName"
          :loading="loadingSoftwareLicenseName">
          <template #label>
            <div class="flex gap-2">
              <ApplicationIcon
                v-if="applicationName && props.tagStyle === 'default'"
                :application-name="applicationName"
                :size="18" />
              <TextMultiline :text="softwareLicenseName" />
            </div>
          </template>
        </SmTag>
      </template>

      <!-- Group Name -->
      <template v-if="groupName || loadingGroupName" #group>
        <SmTag
          :appearance="props.tagStyle"
          :label="groupName"
          :icon="Users"
          :loading="loadingGroupName" />
      </template>

      <!-- Specific message addition -->
      <template
        v-if="props.type == BackgroundJobType.SOFTWARE_USER_REMOVE"
        #action>
        <TextMultiline
          v-if="'delete' in props.data && props.data.delete"
          :text="
            i18n.locale.value === 'en'
              ? i18n.t('delete')
              : i18n.t('delete').toLowerCase()
          " />
        <TextMultiline
          v-else-if="'disable' in props.data && props.data.disable"
          :text="
            i18n.locale.value === 'en'
              ? i18n.t('general.disable')
              : i18n.t('general.disable').toLowerCase()
          " />
      </template>
    </i18n-t>
  </div>

  <PeopleDrawer
    v-if="userDetailsDrawerVisibility && userDetailsDrawerUserId"
    v-model:drawer-visibility="userDetailsDrawerVisibility"
    :person-id="userDetailsDrawerUserId"
    @close="userDetailsDrawerUserId = undefined" />
</template>

<script setup lang="ts">
  import { LayoutGrid, User, File, Users } from 'lucide-vue-next'
  import {
    BackgroundJobAccountGroupAccountsAddedData,
    BackgroundJobAccountGroupAccountsRemovedData,
    BackgroundJobSoftwareUserAddData,
    BackgroundJobSoftwareUserEnableData,
    BackgroundJobSoftwareUserLicenseAddData,
    BackgroundJobSoftwareUserLicenseRemoveData,
    BackgroundJobSoftwareUserRemoveData,
    BackgroundJobType,
    SoftwareLicensesService,
    UserGroupsService,
  } from '@/client'
  import { AccountStore } from '@/stores/account/account.store'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { PersonStore } from '@/stores/person/person.store'
  import { useI18n } from 'vue-i18n'

  const i18n = useI18n()

  // Define the props interface
  interface Props {
    type: BackgroundJobType
    data:
      | BackgroundJobSoftwareUserAddData
      | BackgroundJobSoftwareUserRemoveData
      | BackgroundJobSoftwareUserLicenseRemoveData
      | BackgroundJobSoftwareUserLicenseAddData
      | BackgroundJobSoftwareUserEnableData
      | BackgroundJobAccountGroupAccountsRemovedData
      | BackgroundJobAccountGroupAccountsAddedData
    clickable?: boolean
    allowDisplayInMultipleLines?: boolean
    tagStyle?: 'default' | 'text'
  }

  // Use withDefaults to set default values for optional props
  const props = withDefaults(defineProps<Props>(), {
    clickable: false,
    allowDisplayInMultipleLines: false,
    tagStyle: 'default',
  })

  onMounted(async () => {
    if ('account_id' in props.data) loadUserName(props.data.account_id)
    if ('software_id' in props.data) loadApplicationName(props.data.software_id)
    if ('licence_id' in props.data) loadLicenseEmail(props.data.licence_id)
    if ('software_license_id' in props.data)
      loadSoftwareLicenseName(props.data.software_license_id)
    if ('account_group_id' in props.data)
      loadGroupName(props.data.account_group_id)
    if ('accounts_removed' in props.data && props.data.accounts_removed)
      loadUserName(props.data.accounts_removed[0])
    if ('accounts_added' in props.data && props.data.accounts_added)
      loadUserName(props.data.accounts_added[0])
  })

  const userName = ref<string>()
  const userId = ref<string>('')
  const loadingUserName = ref(false)
  const applicationName = ref<string>()
  const loadingApplicationName = ref(false)
  const licenseEmail = ref<string>()
  const loadingLicenseEmail = ref(false)
  const softwareLicenseName = ref<string>()
  const loadingSoftwareLicenseName = ref(false)
  const groupName = ref<string>()
  const loadingGroupName = ref(false)

  /**
   * Load Data
   */

  async function loadUserName(id: string) {
    loadingUserName.value = true
    const cache = await PersonStore.getPersonById(id)
    userName.value = cache?.name || undefined
    userId.value = cache?._id || ''
    loadingUserName.value = false
  }

  async function loadApplicationName(id: string) {
    const cache = await ApplicationStore.getApplicationById(id)
    loadingApplicationName.value = true
    applicationName.value = cache?.software_name || undefined
    loadingApplicationName.value = false
  }

  async function loadLicenseEmail(id: string) {
    const cache = await AccountStore.getAccountById(id)
    loadingLicenseEmail.value = true

    licenseEmail.value = cache?.email || undefined
    loadingLicenseEmail.value = false

    // always load software when license is loaded as well
    if (cache?.software_id) loadApplicationName(cache?.software_id)
  }

  async function loadGroupName(id: string) {
    loadingGroupName.value = true
    const group =
      await UserGroupsService.getAccountGroupApiV1ManagementGroupsAccountGroupIdGet(
        {
          accountGroupId: id,
          includeDeleted: true,
        }
      )
    groupName.value = group?.name || undefined
    loadingGroupName.value = false
  }

  async function loadSoftwareLicenseName(id: string) {
    loadingSoftwareLicenseName.value = true

    const softwareLicense =
      await SoftwareLicensesService.getSoftwareLicenseApiV1SoftwareSoftwareLicensesSoftwareLicenseIdGet(
        {
          softwareLicenseId: id,
        }
      )
    softwareLicenseName.value = softwareLicense?.name || undefined
    loadingSoftwareLicenseName.value = false

    // always load software when software license is loaded as well
    if (softwareLicense.software_id)
      loadApplicationName(softwareLicense.software_id)
  }

  /**
   * Get localization string
   */

  function getMessage() {
    switch (props.type) {
      case BackgroundJobType.SOFTWARE_USER_ADD:
        return 'jobMessage.software_user_add'
      case BackgroundJobType.SOFTWARE_USER_REMOVE:
        return 'jobMessage.software_user_remove'
      case BackgroundJobType.SOFTWARE_USER_LICENSE_REMOVE:
        return 'jobMessage.software_user_license_remove'
      case BackgroundJobType.SOFTWARE_USER_LICENSE_ADD:
        return 'jobMessage.software_user_license_add'
      case BackgroundJobType.SOFTWARE_USER_ENABLE:
        return 'jobMessage.software_user_enable'
      case BackgroundJobType.ACCOUNT_GROUP_ACCOUNTS_REMOVED:
        return 'jobMessage.account_group_accounts_removed'
      case BackgroundJobType.ACCOUNT_GROUP_ACCOUNTS_ADDED:
        return 'jobMessage.account_group_accounts_added'
      case BackgroundJobType.PROCESS_ACCOUNTING_IMPORT:
        return 'jobMessage.process_accounting_import'
      default:
        return ''
    }
  }

  /**
   * Detail Drawers
   */
  const userDetailsDrawerVisibility = ref(false)
  const userDetailsDrawerUserId = ref<string | undefined>()

  function openUserDetailsDrawer(id: string) {
    userDetailsDrawerUserId.value = id
    userDetailsDrawerVisibility.value = true
  }
</script>
